'use client';

import React, { useState, useCallback } from 'react';
import {
  Plus, Trash2, ChevronDown, ChevronUp, Receipt, Sparkles, Loader2, CheckCircle2, Check, X, AlertTriangle, Copy,
} from 'lucide-react';
import type { DocumentTaxRate } from '@/components/finance/modals/SortableLineItem';
import type { DocumentLineItem } from '@/hooks/useDocumentsWorkItems';
import {
  type ExpenseAccountOption,
  type AllocationType,
  ALL_ALLOCATION_TYPES,
  ALL_ALLOCATION_OPTIONS,
  getAllowedAllocationTypes,
} from '@/lib/finance/expenseAccountMapping';
import { SearchableAccountSelect } from '@/components/shared/finance/SearchableAccountSelect';
import { AllocationDimensionEditor } from '@/components/shared/finance/AllocationDimensionEditor';
import { SplitAllocationEditor } from '@/components/shared/finance/SplitAllocationEditor';
import { useExpenseLineItems } from '@/hooks/useExpenseLineItems';
import { FULL_TRUST_CAPABILITIES, type CaptureCapabilities } from './captureSchema';
import type { DetectDuplicateLineItemsResult } from './lineItemDedup';

interface ExpenseLineItemsEditorProps {
  expenseLineItems: DocumentLineItem[];
  setExpenseLineItems: React.Dispatch<React.SetStateAction<DocumentLineItem[]>>;
  showAddLineItem: boolean;
  setShowAddLineItem: React.Dispatch<React.SetStateAction<boolean>>;
  newLineItem: { description: string; quantity: number; unit_price: number; tax_rate_id: number | undefined };
  setNewLineItem: React.Dispatch<React.SetStateAction<{ description: string; quantity: number; unit_price: number; tax_rate_id: number | undefined }>>;
  taxRates: DocumentTaxRate[];
  preferredDefaultTaxRate: DocumentTaxRate | null;
  accent: string;
  expenseAccounts?: ExpenseAccountOption[];
  /** Vendor name for AI categorization context. */
  vendorName?: string;
  /** Total amount for AI categorization context. */
  totalAmount?: number;
  /** Capability flags controlling GL allocation, AI suggestions, etc. */
  capabilities?: CaptureCapabilities;
  /** Intra-receipt duplicate line items detected from overlapping multi-photo capture. */
  lineItemDuplicates?: DetectDuplicateLineItemsResult;
  /** Remove all flagged duplicate line items (keeps first occurrence of each). */
  onRemoveDuplicates?: () => void;
  /** Dismiss the intra-receipt duplicate warning without removing items. */
  onDismissDuplicates?: () => void;
}

export function ExpenseLineItemsEditor({
  expenseLineItems,
  setExpenseLineItems,
  showAddLineItem,
  setShowAddLineItem,
  newLineItem,
  setNewLineItem,
  taxRates,
  preferredDefaultTaxRate,
  accent,
  expenseAccounts = [],
  vendorName,
  totalAmount,
  capabilities = FULL_TRUST_CAPABILITIES,
  lineItemDuplicates,
  onRemoveDuplicates,
  onDismissDuplicates,
}: ExpenseLineItemsEditorProps) {
  const [expanded, setExpanded] = useState(true);
  // Per-line-item expand state. When collapsed (default), each line shows as a
  // compact single-row summary so long receipts (multi-page A4 / long till
  // slips captured across several photos) remain scannable. Expanding a row
  // reveals the full editor (GL account, allocation, tax rate, etc.).
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const {
    aiLoading,
    aiSuggestions,
    aiApplied,
    rejectedSuggestions,
    handleAiSuggestGl,
    handleAcceptSuggestion,
    handleRejectSuggestion,
    handleAcceptAll,
    handleRejectAll,
    allocationDimensions,
    getLineItemDimensions,
    getLineItemSplits,
    updateLineItemAllocations,
    expandedAllocations,
    toggleExpandedAllocations,
    handleAddLineItem: hookAddLineItem,
    handleRemoveLineItem,
    handleUpdateLineItem,
    handleUpdateTaxRate,
    computedTotal,
  } = useExpenseLineItems({
    expenseLineItems,
    setExpenseLineItems,
    expenseAccounts,
    vendorName,
    totalAmount,
    taxRates,
  });

  const handleAddLineItem = () => {
    if (hookAddLineItem(newLineItem)) {
      setNewLineItem({ description: '', quantity: 1, unit_price: 0, tax_rate_id: undefined });
      setShowAddLineItem(false);
    }
  };

  const toggleItemExpanded = useCallback((idx: number) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const allItemsExpanded = expandedItems.size === expenseLineItems.length && expenseLineItems.length > 0;
  const setAllItemsExpanded = useCallback((expand: boolean) => {
    if (expand) {
      setExpandedItems(new Set(expenseLineItems.map((_, idx) => idx)));
    } else {
      setExpandedItems(new Set());
    }
  }, [expenseLineItems]);

  const hasLineItemDuplicates = !!(lineItemDuplicates && lineItemDuplicates.duplicateCount > 0);

  return (
    <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      {/* Header */}
      <div
        className="flex items-center justify-between p-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <Receipt className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-900 dark:text-white">
            Line Items ({expenseLineItems.length})
          </span>
          {expenseLineItems.length > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Total: R{computedTotal.toFixed(2)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {expenseLineItems.length > 0 && expenseAccounts.length > 0 && capabilities.canUseAiGlSuggestions && (
            <button
              type="button"
              disabled={aiLoading}
              onClick={(e) => {
                e.stopPropagation();
                handleAiSuggestGl();
              }}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-white"
              style={{ backgroundColor: '#7c3aed' }}
              title="Use AI to suggest GL account codes for each line item"
            >
              {aiLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              {aiLoading ? 'Suggesting…' : 'AI GL Codes'}
            </button>
          )}
          {aiApplied && Object.keys(aiSuggestions).length > 0 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAcceptAll();
                }}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:text-emerald-300 dark:bg-emerald-900/20 dark:hover:bg-emerald-900/30"
                title="Accept all AI suggestions (≥60% confidence)"
              >
                <Check className="h-3 w-3" />
                Accept All
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRejectAll();
                }}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
                title="Reject all AI suggestions"
              >
                <X className="h-3 w-3" />
                Reject All
              </button>
            </>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowAddLineItem(!showAddLineItem);
            }}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-white"
            style={{ backgroundColor: accent }}
          >
            <Plus className="h-3 w-3" />
            Add
          </button>
          {expenseLineItems.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setAllItemsExpanded(!allItemsExpanded);
              }}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
              title={allItemsExpanded ? 'Collapse all line items to compact rows' : 'Expand all line items to edit details'}
            >
              {allItemsExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {allItemsExpanded ? 'Collapse all' : 'Expand all'}
            </button>
          )}
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Zero-trust duplicate warning — always visible (not gated by expanded state) */}
      {hasLineItemDuplicates && !capabilities.canAllocateGl && (
        <div className="mx-3 mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-900/20">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                {lineItemDuplicates!.duplicateCount} duplicate line item{lineItemDuplicates!.duplicateCount !== 1 ? 's' : ''} detected
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                Possible duplicates from overlapping photos of a long receipt. {lineItemDuplicates!.duplicateCount} item{lineItemDuplicates!.duplicateCount !== 1 ? 's' : ''} will be removed automatically on submission.
              </p>
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={onRemoveDuplicates}
                  className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-amber-700 dark:bg-amber-700 dark:hover:bg-amber-600"
                >
                  <Trash2 className="h-3 w-3" />
                  Remove duplicates
                </button>
                <button
                  type="button"
                  onClick={() => setExpanded(!expanded)}
                  className="rounded-md px-2.5 py-1 text-xs font-medium text-amber-700 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100"
                >
                  Review items
                </button>
                <button
                  type="button"
                  onClick={onDismissDuplicates}
                  className="rounded-md px-2.5 py-1 text-xs font-medium text-amber-700 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100"
                >
                  Keep all
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          {/* Intra-receipt duplicate banner (overlapping multi-photo capture) */}
          {hasLineItemDuplicates && capabilities.canAllocateGl && (
            <div className="m-3 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-900/20">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                    {lineItemDuplicates!.duplicateCount} duplicate line item{lineItemDuplicates!.duplicateCount !== 1 ? 's' : ''} detected
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                    These look like overlapping items from multiple photos of the same long receipt. Review the highlighted rows and remove the duplicates before submitting.
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={onRemoveDuplicates}
                      className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-amber-700 dark:bg-amber-700 dark:hover:bg-amber-600"
                    >
                      <Trash2 className="h-3 w-3" />
                      Remove duplicates
                    </button>
                    <button
                      type="button"
                      onClick={onDismissDuplicates}
                      className="rounded-md px-2.5 py-1 text-xs font-medium text-amber-700 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100"
                    >
                      Keep all
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {expenseLineItems.length === 0 && !showAddLineItem ? (
            <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
              No line items added. The total amount will be used as a single line item.
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {expenseLineItems.map((item, idx) => {
                const qty = Number(item.quantity) || 0;
                const price = Number(item.unit_price) || 0;
                const lineTotal = item.line_total != null ? Number(item.line_total) : (qty * price);
                const isDuplicate = !!(lineItemDuplicates && lineItemDuplicates.duplicateIndexSet.has(idx));
                const isItemExpanded = expandedItems.has(idx);
                return (
                  <div
                    key={idx}
                    className={
                      isDuplicate
                        ? 'bg-amber-50/60 dark:bg-amber-900/10'
                        : ''
                    }
                  >
                    {/* Compact summary row — always visible. Click to toggle expand. */}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleItemExpanded(idx)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleItemExpanded(idx);
                        }
                      }}
                      className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40"
                    >
                      {isItemExpanded ? (
                        <ChevronUp className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                      )}
                      <span className="w-6 flex-shrink-0 text-[11px] font-medium text-gray-400 dark:text-gray-500">
                        {idx + 1}
                      </span>
                      <span className="flex-1 min-w-0 truncate text-sm text-gray-900 dark:text-white" title={item.description}>
                        {item.description || <span className="text-gray-400 italic">No description</span>}
                      </span>
                      {isDuplicate && (
                        <span
                          className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 flex-shrink-0"
                          title="Possible duplicate from overlapping photos"
                        >
                          <Copy className="h-2.5 w-2.5" />
                          Duplicate
                        </span>
                      )}
                      <span className="hidden sm:inline text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                        {qty} × R{price.toFixed(2)}
                      </span>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex-shrink-0 tabular-nums">
                        R{lineTotal.toFixed(2)}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveLineItem(idx);
                          setExpandedItems((prev) => {
                            const next = new Set(prev);
                            next.delete(idx);
                            return next;
                          });
                        }}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 flex-shrink-0"
                        title="Remove line item"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    {/* Expanded detail editor — shown when the row is toggled open. */}
                    {isItemExpanded && (
                    <div className="px-3 pb-3 space-y-2">
                      <div className="flex items-start gap-2">
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleUpdateLineItem(idx, 'description', e.target.value)}
                          placeholder="Description"
                          className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            handleRemoveLineItem(idx);
                            setExpandedItems((prev) => {
                              const next = new Set(prev);
                              next.delete(idx);
                              return next;
                            });
                          }}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                        >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Qty</label>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleUpdateLineItem(idx, 'quantity', Number(e.target.value))}
                          className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Unit Price</label>
                        <input
                          type="number"
                          value={item.unit_price ?? 0}
                          onChange={(e) => handleUpdateLineItem(idx, 'unit_price', Number(e.target.value))}
                          className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Total</label>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-sm font-medium text-gray-700 dark:border-gray-700 dark:bg-gray-700/50 dark:text-gray-300">
                          R{lineTotal.toFixed(2)}
                        </div>
                      </div>
                    </div>
                    {/* AI Suggestion Banner */}
                    {aiSuggestions[idx] && !rejectedSuggestions.has(idx) &&
                      item.expense_account_code !== aiSuggestions[idx].account_code && (
                      <div className="flex items-center justify-between rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 dark:border-violet-700 dark:bg-violet-900/20">
                        <div className="flex items-center gap-2 text-xs">
                          <Sparkles className="h-3 w-3 text-violet-500" />
                          <span className="font-medium text-violet-700 dark:text-violet-300">
                            {aiSuggestions[idx].account_code} — {aiSuggestions[idx].account_name}
                          </span>
                          <span className={`rounded px-1 py-0.5 text-[9px] font-semibold ${
                            aiSuggestions[idx].confidence >= 85
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                              : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                          }`}>
                            {aiSuggestions[idx].confidence}%
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleAcceptSuggestion(idx)}
                            className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 bg-emerald-100 hover:bg-emerald-200 dark:text-emerald-300 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/50"
                          >
                            <Check className="h-2.5 w-2.5" />
                            Accept
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRejectSuggestion(idx)}
                            className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
                          >
                            <X className="h-2.5 w-2.5" />
                            Reject
                          </button>
                        </div>
                      </div>
                    )}
                    {/* GL Account + Allocation Target — hidden when GL allocation is not permitted */}
                    {expenseAccounts.length > 0 && capabilities.canAllocateGl && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            GL Account
                            {aiSuggestions[idx] && (
                              <span
                                className={`ml-1 inline-flex items-center gap-0.5 text-[10px] font-medium ${
                                  aiSuggestions[idx].confidence >= 85
                                    ? 'text-emerald-600'
                                    : 'text-amber-600'
                                }`}
                                title={`AI confidence: ${aiSuggestions[idx].confidence}% — ${aiSuggestions[idx].account_name}`}
                              >
                                {aiApplied && item.expense_account_code === aiSuggestions[idx].account_code && (
                                  <CheckCircle2 className="h-2.5 w-2.5" />
                                )}
                                AI {aiSuggestions[idx].confidence}%
                              </span>
                            )}
                          </label>
                          <SearchableAccountSelect
                            accounts={expenseAccounts}
                            value={item.expense_account_code ?? ''}
                            onChange={(code) => {
                              const allowed = getAllowedAllocationTypes(expenseAccounts, code);
                              const currentTypeValid = item.allocation_type
                                ? allowed.includes(item.allocation_type)
                                : false;
                              handleUpdateLineItem(idx, 'expense_account_code', code);
                              if (!currentTypeValid) {
                                handleUpdateLineItem(idx, 'allocation_type', allowed[0] ?? 'overhead');
                              }
                            }}
                            placeholder="— Select —"
                            className={
                              aiApplied && aiSuggestions[idx] && item.expense_account_code === aiSuggestions[idx].account_code
                                ? 'border-violet-300 bg-violet-50 dark:border-violet-700 dark:bg-violet-900/20'
                                : ''
                            }
                          />
                          {aiApplied && aiSuggestions[idx] && item.expense_account_code === aiSuggestions[idx].account_code && (
                            <div className="text-[10px] text-violet-600 dark:text-violet-400 mt-0.5">
                              {aiSuggestions[idx].account_name}
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Allocation</label>
                          {item.expense_account_code ? (
                            <select
                              value={item.allocation_type ?? 'overhead'}
                              onChange={(e) => handleUpdateLineItem(idx, 'allocation_type', e.target.value as AllocationType)}
                              className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                            >
                              {ALL_ALLOCATION_OPTIONS.filter(opt =>
                                getAllowedAllocationTypes(expenseAccounts, item.expense_account_code).includes(opt.value as AllocationType)
                              ).map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          ) : (
                            <div className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500">
                              <span>Select GL account first</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Split Allocation Editor — hidden when GL allocation is not permitted */}
                    {expenseAccounts.length > 0 && capabilities.canAllocateGl && (
                      <SplitAllocationEditor
                        accounts={expenseAccounts}
                        splits={getLineItemSplits(idx)}
                        onChange={(splits) => updateLineItemAllocations(idx, { splits })}
                        lineTotal={lineTotal}
                      />
                    )}
                    {/* Multi-Dimensional Allocation Editor (collapsible) — hidden when GL allocation is not permitted */}
                    {expenseAccounts.length > 0 && capabilities.canAllocateGl && (
                      <div>
                        <button
                          type="button"
                          onClick={() => toggleExpandedAllocations(idx)}
                          className="text-[10px] font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          {expandedAllocations.has(idx) ? '− Hide' : '+ Show'} allocation dimensions
                          {(getLineItemDimensions(idx))?.length > 0 && (
                            <span className="ml-1 rounded-full bg-blue-100 px-1 py-0.5 text-[8px] font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                              {getLineItemDimensions(idx).length}
                            </span>
                          )}
                        </button>
                        {expandedAllocations.has(idx) && (
                          <AllocationDimensionEditor
                            dimensions={allocationDimensions}
                            allocations={getLineItemDimensions(idx)}
                            onChange={(allocs) => updateLineItemAllocations(idx, { dimensions: allocs })}
                            lineTotal={lineTotal}
                          />
                        )}
                      </div>
                    )}
                    {/* Tax rate selector */}
                    {taxRates.length > 0 && (
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Tax Rate</label>
                        <select
                          value={item.tax_rate_id ?? ''}
                          onChange={(e) => {
                            const taxRateId = e.target.value ? Number(e.target.value) : undefined;
                            handleUpdateTaxRate(idx, taxRateId, taxRates);
                          }}
                          className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                        >
                          <option value="">
                            {item.tax_rate != null ? `${item.tax_rate}%` : '15%'}
                          </option>
                          {taxRates.map(rate => (
                            <option key={rate.id} value={rate.id}>
                              {rate.name} ({rate.rate}%)
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    </div>
                    )}
                  </div>
                );
              })}

              {/* Add new line item form */}
              {showAddLineItem && (
                <div className="p-3 space-y-2 bg-gray-50 dark:bg-gray-800/50">
                  <input
                    type="text"
                    value={newLineItem.description}
                    onChange={(e) => setNewLineItem({ ...newLineItem, description: e.target.value })}
                    placeholder="Description"
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Qty</label>
                      <input
                        type="number"
                        value={newLineItem.quantity}
                        onChange={(e) => setNewLineItem({ ...newLineItem, quantity: Number(e.target.value) })}
                        className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Unit Price</label>
                      <input
                        type="number"
                        value={newLineItem.unit_price}
                        onChange={(e) => setNewLineItem({ ...newLineItem, unit_price: Number(e.target.value) })}
                        className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                  </div>
                  {taxRates.length > 0 && (
                    <select
                      value={newLineItem.tax_rate_id ?? ''}
                      onChange={(e) => setNewLineItem({ ...newLineItem, tax_rate_id: e.target.value ? Number(e.target.value) : undefined })}
                      className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    >
                      <option value="">Default ({preferredDefaultTaxRate ? `${preferredDefaultTaxRate.rate}%` : '15%'})</option>
                      {taxRates.map(rate => (
                        <option key={rate.id} value={rate.id}>
                          {rate.name} ({rate.rate}%)
                        </option>
                      ))}
                    </select>
                  )}
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddLineItem(false);
                        setNewLineItem({ description: '', quantity: 1, unit_price: 0, tax_rate_id: undefined });
                      }}
                      className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleAddLineItem}
                      disabled={!newLineItem.description.trim()}
                      className="rounded-lg px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                      style={{ backgroundColor: accent }}
                    >
                      Add Item
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
